import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Team() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent" });
  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); api.get("/users").then(({ data }) => setRows(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try { await api.post("/users", form); setAddOpen(false); setForm({ name: "", email: "", password: "", role: "agent" }); load(); toast.success("User added"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const changeRole = async (u, role) => { try { await api.put(`/users/${u.id}`, { role }); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const toggleActive = async (u) => { try { await api.put(`/users/${u.id}`, { active: !u.active }); load(); toast.success(u.active ? "Deactivated" : "Reactivated"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const resetPw = async () => { try { await api.put(`/users/${pwUser.id}`, { password: pw }); setPwUser(null); setPw(""); toast.success("Password reset"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const del = async (u) => { if (!window.confirm(`Delete ${u.name}?`)) return; try { await api.delete(`/users/${u.id}`); load(); toast.success("Deleted"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <div>
      <PageHeader title="Team" subtitle="Admins manage every account. There is no public sign-up.">
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-user-btn"><Plus className="h-4 w-4" /> Add member</Button>
      </PageHeader>

      <div className="px-8 py-6">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0047FF]" /></div> : (
            <Table>
              <TableHeader><TableRow className="bg-gray-50"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {rows.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.id === user.id}>
                        <SelectTrigger className="w-28 h-8" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white"><SelectItem value="admin">Admin</SelectItem><SelectItem value="agent">Agent</SelectItem></SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <button onClick={() => toggleActive(u)} disabled={u.id === user.id} data-testid={`active-${u.id}`}
                        className={`text-xs px-2 py-0.5 rounded border ${u.active ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"} disabled:opacity-50`}>
                        {u.active ? "Active" : "Inactive"}
                      </button>
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setPwUser(u)} data-testid={`reset-${u.id}`}><KeyRound className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(u)} disabled={u.id === user.id} data-testid={`delete-${u.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white" data-testid="add-user-dialog">
          <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="u-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="u-email" /></div>
            <div><Label>Temporary password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="u-password" /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="u-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="admin">Admin</SelectItem><SelectItem value="agent">Agent</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy || !form.email || !form.password} data-testid="u-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwUser} onOpenChange={(v) => !v && setPwUser(null)}>
        <DialogContent className="bg-white" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle>Reset password for {pwUser?.name}</DialogTitle></DialogHeader>
          <Input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 6 chars)" data-testid="new-pw-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwUser(null)}>Cancel</Button>
            <Button onClick={resetPw} disabled={pw.length < 6} data-testid="confirm-reset-btn">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
